import { useEffect, useRef, useState } from 'react';

interface ProgressBarProps {
  percent: number;
  /** When true, bytesTotal was unknown so we show an indeterminate bar. */
  indeterminate?: boolean;
  bytesTransferred?: number;
  bytesTotal?: number | null;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function ProgressBar({
  percent,
  indeterminate = false,
  bytesTransferred,
  bytesTotal,
}: ProgressBarProps) {
  const pct = Math.min(100, Math.max(0, percent));
  const [displayPct, setDisplayPct] = useState(0);
  const rafRef = useRef<number | null>(null);
  const fromRef = useRef(0);

  useEffect(() => {
    const from = fromRef.current;
    const to = pct;
    if (from === to) return;

    const duration = 600;
    const start = performance.now();

    function tick(now: number) {
      const t = Math.min((now - start) / duration, 1);
      const eased = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
      const current = from + (to - from) * eased;
      setDisplayPct(current);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = to;
      }
    }

    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current !== null) cancelAnimationFrame(rafRef.current); };
  }, [pct]);

  return (
    <div className="flex items-center gap-2 min-w-[200px]">
      <div className="flex-1 h-1.5 bg-[#e9e9e7] rounded-full overflow-hidden">
        {indeterminate ? (
          <div className="h-full w-1/3 bg-[#2383e2] rounded-full animate-[slide_1.5s_ease-in-out_infinite]" />
        ) : (
          <div
            className="h-full bg-[#2383e2] rounded-full transition-none"
            style={{ width: `${displayPct}%` }}
          />
        )}
      </div>
      <span className="text-xs tabular-nums whitespace-nowrap text-[#73726e]">
        {indeterminate
          ? '—'
          : bytesTransferred !== undefined
            ? `${formatBytes(bytesTransferred)}${bytesTotal != null ? ` / ${formatBytes(bytesTotal)}` : ''}`
            : `${Math.round(displayPct)}%`}
      </span>
    </div>
  );
}
